import { tradeRequestApi } from 'apiConnector/quote/trade';
import * as apiErrorHelper from 'epic/apiErrorEpicHelper';
import { quoteOrTabRemoved } from 'epic/workspace/common/tabs/tabsEpics/quoteOrTabRemoved';
import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { EMPTY, merge, of } from 'rxjs';
import { catchError, filter, map, mergeMap, startWith, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import type { FunkyAction } from 'shared/middlewares/epic/epicMiddleware.factory';
import { Action } from 'state/actions';
import {
  addonQuoteTradeAck,
  addonQuoteTradeDone,
  addonQuoteTradeFailed,
  addonQuoteTradeRejected,
  addonQuoteTradeRequestRejected,
  addonQuoteTradeSent,
} from 'state/addonForm/addonForm.action';
import * as actionTypes from 'state/addonForm/addonForm.actionTypes';
import type { TradeExecReport } from 'state/quotes/trades/trades.model';
import type { EpicWithThunk } from 'state/state.common';
import { throwOnTimeout, wrapError } from 'utils/rxjs';
import { fatEventApiConnector } from 'utils/streaming/api';
import { tradeExecutionReport } from 'utils/streaming/messages';

function handleTradeReport(
  tabId: string,
  quoteId: string,
  { status, errors, execMessages }: TradeExecReport,
): Observable<FunkyAction> {
  switch (status) {
    case 'FULLY_EXECUTED':
      return of(addonQuoteTradeDone(tabId, quoteId, 'FULLY_EXECUTED', execMessages));
    case 'CANCELLED':
    case 'EXPIRED':
    case 'FAILED':
    case 'REJECTED':
      return of(addonQuoteTradeRejected(tabId, quoteId, errors!));
    default:
      return EMPTY;
  }
}
function handleError(error: Error, tabId: string, quoteId: string): Observable<Action> {
  if (apiErrorHelper.isHttpError(error) && error.status === 400) {
    return of(addonQuoteTradeRequestRejected(tabId, quoteId, error.response));
  }
  return of(
    addonQuoteTradeFailed(
      tabId,
      quoteId,
      apiErrorHelper.toApiErrorMessage(error, {
        timeoutErrorMessage: 'trade.errorMessages.requestTimeout',
      }),
      error,
    ),
  );
}
export function requestTradeEpic(): EpicWithThunk {
  return (action$, state$, { oldHttpClient, selectors: { addonSelector }, getSgmeConfiguration }) =>
    action$.pipe(
      ofType(actionTypes.ADDON_QUOTE_TRADE_REQUESTED),
      withLatestFrom(state$),
      mergeMap(([{ tabId }, state]) => {
        const quoteId = addonSelector.getLastQuoteId(state, tabId)!;
        const tradeRequest = addonSelector.getTradeRequest(state, tabId);
        const tradeAckTimeoutMs = getSgmeConfiguration().tradeAckTimeoutMs;

        return tradeRequestApi(oldHttpClient, tradeAckTimeoutMs)
          .requestTrade(tradeRequest)
          .pipe(
            map(() => addonQuoteTradeAck(tabId, quoteId)),
            catchError(err => handleError(err, tabId, quoteId)),
          );
      }),
    );
}

export function handleTradeAckEpic(appScheduler?: SchedulerLike): EpicWithThunk {
  return (action$, state$, { oldHttpClient, getSgmeConfiguration, streaming }) =>
    action$.pipe(
      ofType('ADDON_QUOTE_TRADE_ACK'),
      mergeMap(({ tabId, quoteId }) => {
        const tradeExecReportTimeoutMs = getSgmeConfiguration().tradeExecReportTimeoutMs;
        const quoteOrTabRemoved$ = quoteOrTabRemoved(action$, tabId, quoteId, state$);

        const tradeFailed$ = action$.pipe(
          ofType(actionTypes.ADDON_QUOTE_TRADE_FAILED),
          filter(a => a.tabId === tabId),
        );
        return tradeExecutionReport(streaming).pipe(
          takeUntil(merge(tradeFailed$, quoteOrTabRemoved$)),
          filter(r => r.quoteId === quoteId),
          mergeMap(lightEvent =>
            fatEventApiConnector(oldHttpClient)
              .getFatEvent<TradeExecReport>(lightEvent)
              .pipe(
                mergeMap(report => handleTradeReport(tabId, quoteId, report)),
                wrapError(
                  error => new apiErrorHelper.EpicApiError('trade.errorMessages.executionReportTechnicalError', error),
                  appScheduler,
                ),
              ),
          ),
          take(1),
          throwOnTimeout(
            tradeExecReportTimeoutMs,
            new apiErrorHelper.TimeoutError(tradeExecReportTimeoutMs, 'trade.errorMessages.executionReportTimeout'),
            appScheduler,
          ),
          startWith(addonQuoteTradeSent(tabId, quoteId)),
          catchError(err => handleError(err, tabId, quoteId)),
        );
      }),
    );
}
